import { useMemo } from 'react';

// Board-specific types
export interface BoardPlayer {
  id: string;
  name: string;
  color: 'red' | 'blue' | 'green' | 'yellow';
  pieces: Array<{
    id: string;
    position: number;
    isInHomeStretch: boolean;
    hasCompletedCircuit: boolean;
    lastMoveId: string;
  }>;
}

export interface BoardGameState {
  players: BoardPlayer[];
  currentPlayer: number;
  diceValue: number | null;
  gameStatus: 'waiting' | 'playing' | 'finished';
  winner: string | null;
}

export interface PieceOnPosition {
  player: BoardPlayer;
  pieceIndex: number;
}

export const useBoardState = (
  gameState: BoardGameState,
  validMoves: number[] = []
) => {
  // Starting positions for safe squares
  const STARTING_POSITIONS = useMemo(() => [0, 13, 26, 39], []);

  // Get pieces at a specific position
  const getPiecesAtPosition = useMemo(() => {
    return (
      position: number,
      isHomeStretch: boolean = false,
      homeStretchColor?: string
    ): PieceOnPosition[] => {
      const piecesOnPosition: PieceOnPosition[] = [];

      gameState.players.forEach((player) => {
        player.pieces.forEach((piece, pieceIndex) => {
          if (isHomeStretch) {
            // For home stretch cells, match pieces that are in home stretch with same position
            if (
              piece.isInHomeStretch &&
              piece.position === position &&
              player.color === homeStretchColor
            ) {
              piecesOnPosition.push({ player, pieceIndex });
            }
          } else {
            // For main circuit cells, match pieces that are NOT in home stretch with same position
            if (!piece.isInHomeStretch && piece.position === position) {
              piecesOnPosition.push({ player, pieceIndex });
            }
          }
        });
      });

      return piecesOnPosition;
    };
  }, [gameState.players]);

  // Check if a position is safe
  const isSafePosition = useMemo(() => {
    return (position: number, isHomeStretch: boolean = false): boolean => {
      return STARTING_POSITIONS.includes(position) && !isHomeStretch;
    };
  }, [STARTING_POSITIONS]);

  // Check if a position is a home entry position
  const isHomeEntryPosition = useMemo(() => {
    return (position: number): boolean => {
      return (
        position === 51 || // Red home entry
        position === 12 || // Green home entry
        position === 25 || // Yellow home entry
        position === 38
      ); // Blue home entry
    };
  }, []);

  // Get the current player
  const currentPlayer = useMemo(() => {
    return gameState.players[gameState.currentPlayer];
  }, [gameState.players, gameState.currentPlayer]);

  // Check if a piece should be highlighted
  const shouldHighlightPiece = useMemo(() => {
    return (playerId: string, pieceIndex: number): boolean => {
      const isCurrentPlayerPiece = currentPlayer?.id === playerId;
      return isCurrentPlayerPiece && validMoves.includes(pieceIndex);
    };
  }, [currentPlayer, validMoves]);

  // Get color class for a position
  const getPositionColorClass = useMemo(() => {
    return (
      position: number,
      isHomeStretch: boolean = false,
      colorClass: string = ''
    ): string => {
      if (isSafePosition(position) && !isHomeStretch) {
        // Apply lighter player colors to starting positions
        switch (position) {
          case 0:
            return 'red-light';
          case 13:
            return 'green-light';
          case 26:
            return 'yellow-light';
          case 39:
            return 'blue-light';
          default:
            return colorClass;
        }
      } else if (isHomeStretch && colorClass) {
        // Apply normal colors for home stretch squares
        return colorClass;
      }
      return colorClass;
    };
  }, [isSafePosition]);

  return {
    STARTING_POSITIONS,
    getPiecesAtPosition,
    isSafePosition,
    isHomeEntryPosition,
    currentPlayer,
    shouldHighlightPiece,
    getPositionColorClass,
  };
};
